import { TimePeriod } from '../../../enum';

export type SalesGranularity = 'daily' | 'weekly' | 'biweekly' | 'monthly';

export type TrendPoint = {
  date: string;
  endDate?: string;
  value?: number;
  count?: number;
};

function sumField(points: TrendPoint[], field: 'value' | 'count') {
  return points.reduce((acc, p) => acc + (p[field] ?? 0), 0);
}

function aggregateMonthly<T extends TrendPoint>(
  points: T[],
  field: 'value' | 'count'
): T[] {
  const buckets = new Map<string, T>();
  const order: string[] = [];

  for (const point of points) {
    const monthKey = point.date.slice(0, 7);
    const existing = buckets.get(monthKey);

    if (!existing) {
      order.push(monthKey);
      buckets.set(monthKey, {
        ...point,
        date: point.date,
        endDate: point.date,
        [field]: point[field] ?? 0,
      } as T);
      continue;
    }

    (existing as TrendPoint).endDate = point.date;
    (existing as TrendPoint)[field] =
      ((existing as TrendPoint)[field] ?? 0) + (point[field] ?? 0);
  }

  return order
    .map((key) => buckets.get(key))
    .filter((bucket): bucket is T => bucket !== undefined);
}

function bucketEndDate<T extends TrendPoint>(slice: T[]): string {
  if (!slice.length) return '';
  return slice[slice.length - 1]?.date ?? slice[0]?.date ?? '';
}

function aggregateWeekly<T extends TrendPoint>(
  points: T[],
  field: 'value' | 'count',
  bucketCount?: number
): T[] {
  if (!bucketCount || bucketCount < 1) {
    const chunks: T[] = [];
    for (let i = 0; i < points.length; i += 7) {
      const slice = points.slice(i, i + 7);
      chunks.push({
        ...slice[0],
        date: slice[0].date,
        endDate: bucketEndDate(slice),
        [field]: sumField(slice, field),
      } as T);
    }
    return chunks;
  }

  const buckets: T[] = [];
  let start = 0;
  let remainder = points.length % bucketCount;
  const baseSize = Math.floor(points.length / bucketCount);

  for (let i = 0; i < bucketCount; i++) {
    const size = baseSize + (remainder > 0 ? 1 : 0);
    if (remainder > 0) remainder--;
    const slice = points.slice(start, start + size);
    start += size;

    buckets.push({
      ...(slice[0] ?? points[points.length - 1]),
      date: slice[0]?.date ?? points[points.length - 1]?.date ?? '',
      endDate: bucketEndDate(slice),
      [field]: sumField(slice, field),
    } as T);
  }

  return buckets;
}

/** Group daily points into aligned buckets so current/previous series stay comparable. */
export function aggregateTrendSeries<T extends TrendPoint>(
  points: T[] | undefined,
  granularity: SalesGranularity,
  field: 'value' | 'count',
  bucketCount?: number
): T[] {
  if (!points?.length || granularity === 'daily') {
    return points ?? [];
  }

  if (granularity === 'weekly' || granularity === 'biweekly') {
    return aggregateWeekly(points, field, bucketCount);
  }

  const calendarMonths = aggregateMonthly(points, field);

  // Prefer the requested month count (duration-based). Calendar-month grouping
  // often invents extra partial months (14 May–14 Jul → May/Jun/Jul = 3).
  if (bucketCount && bucketCount > 0 && calendarMonths.length !== bucketCount) {
    return aggregateWeekly(points, field, bucketCount);
  }

  return calendarMonths;
}

/** Bucket daily trend data for the selected view across the full time-duration range. */
export function prepareTrendSeriesForView<T extends TrendPoint>(
  points: T[] | undefined,
  granularity: SalesGranularity,
  field: 'value' | 'count',
  bucketCount?: number
): T[] {
  return aggregateTrendSeries(points, granularity, field, bucketCount);
}

/** Keep chart buckets aligned with the selected time-duration day count. */
export function alignSeriesToPeriodDays<T extends TrendPoint>(
  points: T[] | undefined,
  bucketCount: number | undefined,
  _granularity: SalesGranularity
): T[] {
  if (!points?.length || !bucketCount || bucketCount < 1) {
    return points ?? [];
  }

  if (points.length === bucketCount) {
    return points;
  }

  if (points.length > bucketCount) {
    return points.slice(-bucketCount);
  }

  const padded = [...points];
  while (padded.length < bucketCount) {
    padded.unshift({
      date: padded[0]?.date ?? '',
      value: 0,
      count: 0,
    } as T);
  }

  return padded;
}

/** Match previous-period points to the current chart length. */
export function alignPreviousSeries<T extends TrendPoint>(
  current: T[],
  previous: T[] | undefined,
  field: 'value' | 'count'
): T[] {
  if (!previous?.length) {
    return current.map((point) => ({ ...point, [field]: 0 } as T));
  }

  if (previous.length === current.length) {
    return previous;
  }

  if (previous.length > current.length) {
    return previous.slice(-current.length);
  }

  const padded = [...previous];
  while (padded.length < current.length) {
    padded.push({
      date: current[padded.length]?.date ?? '',
      [field]: 0,
    } as T);
  }

  return padded;
}


export function getDailyAxisInterval(pointCount: number): number {
  if (pointCount <= 14) return 1;
  if (pointCount <= 31) return 2;
  if (pointCount <= 60) return 3;
  if (pointCount <= 90) return 5;
  if (pointCount <= 180) return 7;
  return 14;
}

function getOrdinalSuffix(day: number): string {
  if (day > 3 && day < 21) return 'th';
  switch (day % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
}

/** Chart x-axis daily label, e.g. "13th June" or "13th July 2026". */
export function formatChartDailyDate(
  dateStr: string,
  options?: { includeYear?: boolean }
): string {
  const date = parseLocalDate(dateStr);
  if (!date) return dateStr;

  const day = date.getDate();
  const month = date.toLocaleString('default', { month: 'short' });
  const label = `${day}${getOrdinalSuffix(day)} ${month}`;

  if (options?.includeYear) {
    return `${label} ${date.getFullYear()}`;
  }

  return label;
}

export function shouldShowDailyAxisTick(
  index: number,
  pointCount: number,
  points?: TrendPoint[]
): boolean {
  if (pointCount <= 0) return false;
  if (index === 0 || index === pointCount - 1) return true;

  const interval = getDailyAxisInterval(pointCount);
  if (index % interval === 0) return true;

  if (points && index > 0) {
    const current = parseLocalDate(points[index]?.date ?? '');
    const previous = parseLocalDate(points[index - 1]?.date ?? '');
    if (current && previous && current.getMonth() !== previous.getMonth()) {
      return true;
    }
  }

  return false;
}

/**
 * Daily charts that use 1..N on the card x-axis (dates on hover).
 * Applies to Last 30 Days and custom ranges of ~15–31 days.
 * Week-length ranges (Today, Yesterday, Last 7 Days, custom ≤14 days) keep dates on the axis.
 */
export function shouldUseNumberedDailyAxis(
  timeDuration?: string,
  periodDays?: number
): boolean {
  if (timeDuration === TimePeriod.lastThirtyDays) return true;
  if (
    timeDuration === TimePeriod.customRange &&
    periodDays !== undefined &&
    periodDays >= 15 &&
    periodDays <= 31
  ) {
    return true;
  }
  return false;
}

/** Bi-Weekly always uses numbered x-axis; dates only on hover. */
export function shouldUseNumberedBiWeeklyAxis(
  granularity: SalesGranularity
): boolean {
  return granularity === 'biweekly';
}

/** Numbered x-axis (1, 2, 3…) — dates shown on hover only. */
export function shouldUseNumberedAxisTooltips(
  granularity: SalesGranularity,
  timeDuration?: string,
  periodDays?: number
): boolean {
  return (
    shouldUseNumberedBiWeeklyAxis(granularity) ||
    (granularity === 'daily' &&
      shouldUseNumberedDailyAxis(timeDuration, periodDays))
  );
}

export function getNumberedAxisHoverLabel(
  points: TrendPoint[] | undefined,
  index: number,
  granularity: SalesGranularity,
  timeDuration?: string
): string {
  if (!points?.length || index < 0 || index >= points.length) return '';
  const point = points[index];
  return getTrendTooltipPeriodLabel(
    granularity,
    index,
    String(index + 1),
    point?.date,
    undefined,
    timeDuration,
    point?.endDate
  );
}

type AxisHoverHandlers = {
  onShow: (label: string, anchorX: number, anchorY: number) => void;
  onHide: () => void;
};

function hideApexChartTooltip(chartEl: HTMLElement) {
  const tip = chartEl.querySelector<HTMLElement>('.apexcharts-tooltip');
  if (tip) tip.style.display = 'none';
}

function isPointerOverAxisLabel(
  target: EventTarget | null,
  chartEl: HTMLElement
): boolean {
  if (!(target instanceof Element)) return false;
  if (!chartEl.contains(target)) return false;
  return Boolean(
    target.closest('[data-numbered-axis-hit]') ||
      target.closest('.apexcharts-xaxis-texts-g')
  );
}

/** Wire styled hover tooltips onto Apex numbered x-axis labels (1, 2, 3…). */
export function bindNumberedAxisHoverTooltips(
  chartEl: HTMLElement | null | undefined,
  points: TrendPoint[] | undefined,
  categories: string[],
  granularity: SalesGranularity,
  timeDuration: string | undefined,
  periodDays: number | undefined,
  handlers: AxisHoverHandlers
): (() => void) | undefined {
  if (
    !chartEl ||
    !points?.length ||
    !shouldUseNumberedAxisTooltips(granularity, timeDuration, periodDays)
  ) {
    return undefined;
  }

  // Clear any leftover sticky tip when (re)binding
  handlers.onHide();

  // Remove hit rects from a previous bind that Apex may not have cleared
  chartEl
    .querySelectorAll('[data-numbered-axis-hit]')
    .forEach((el) => el.remove());

  const labelNodes = Array.from(
    chartEl.querySelectorAll<SVGTextElement>(
      '.apexcharts-xaxis-texts-g text.apexcharts-xaxis-label'
    )
  );

  const nodes =
    labelNodes.length > 0
      ? labelNodes
      : Array.from(
          chartEl.querySelectorAll<SVGTextElement>(
            '.apexcharts-xaxis-texts-g text'
          )
        );

  const cleanups: Array<() => void> = [];
  const hitRects: SVGRectElement[] = [];
  let tipVisible = false;

  const hideTip = () => {
    if (!tipVisible) return;
    tipVisible = false;
    handlers.onHide();
  };

  nodes.forEach((node, axisIndex) => {
    if (axisIndex >= points.length || axisIndex >= categories.length) return;

    const categoryLabel = (categories[axisIndex] ?? '').trim();
    if (!categoryLabel) return;

    const label = getNumberedAxisHoverLabel(
      points,
      axisIndex,
      granularity,
      timeDuration
    );
    if (!label) return;

    node.querySelectorAll('title').forEach((t) => t.remove());
    node.style.cursor = 'pointer';
    node.style.pointerEvents = 'all';

    // Invisible hit area — SVG <text> mouseleave is unreliable
    const parent = node.parentNode;
    let hitTarget: SVGElement = node;

    if (parent) {
      try {
        const bbox = node.getBBox();
        const hit = document.createElementNS(
          'http://www.w3.org/2000/svg',
          'rect'
        );
        hit.setAttribute('data-numbered-axis-hit', String(axisIndex));
        hit.setAttribute('x', String(bbox.x - 6));
        hit.setAttribute('y', String(bbox.y - 4));
        hit.setAttribute('width', String(Math.max(bbox.width + 12, 16)));
        hit.setAttribute('height', String(Math.max(bbox.height + 10, 18)));
        hit.setAttribute('fill', 'transparent');
        hit.style.cursor = 'pointer';
        hit.style.pointerEvents = 'all';
        parent.insertBefore(hit, node);
        hitRects.push(hit);
        hitTarget = hit;
      } catch {
        // getBBox can fail if node is not rendered yet
      }
    }

    const showTip = () => {
      hideApexChartTooltip(chartEl);
      tipVisible = true;
      const rect = node.getBoundingClientRect();
      handlers.onShow(label, rect.left + rect.width / 2, rect.top);
    };

    const onEnter = () => showTip();
    const onMove = () => showTip();
    const onLeave = (e: MouseEvent) => {
      if (isPointerOverAxisLabel(e.relatedTarget, chartEl)) return;
      hideTip();
    };

    hitTarget.addEventListener('mouseenter', onEnter);
    hitTarget.addEventListener('mousemove', onMove);
    hitTarget.addEventListener('mouseleave', onLeave);
    if (hitTarget !== node) {
      node.addEventListener('mouseenter', onEnter);
      node.addEventListener('mousemove', onMove);
      node.addEventListener('mouseleave', onLeave);
    }

    cleanups.push(() => {
      hitTarget.removeEventListener('mouseenter', onEnter);
      hitTarget.removeEventListener('mousemove', onMove);
      hitTarget.removeEventListener('mouseleave', onLeave);
      if (hitTarget !== node) {
        node.removeEventListener('mouseenter', onEnter);
        node.removeEventListener('mousemove', onMove);
        node.removeEventListener('mouseleave', onLeave);
      }
    });
  });

  // Safety net: hide when pointer leaves the axis labels / chart
  const onDocumentMove = (e: MouseEvent) => {
    if (!tipVisible) return;
    if (!isPointerOverAxisLabel(e.target, chartEl)) {
      hideTip();
    }
  };

  const onChartLeave = () => hideTip();
  const onScroll = () => hideTip();

  document.addEventListener('mousemove', onDocumentMove, true);
  chartEl.addEventListener('mouseleave', onChartLeave);
  window.addEventListener('scroll', onScroll, true);

  cleanups.push(() => {
    document.removeEventListener('mousemove', onDocumentMove, true);
    chartEl.removeEventListener('mouseleave', onChartLeave);
    window.removeEventListener('scroll', onScroll, true);
  });

  return () => {
    hideTip();
    hitRects.forEach((rect) => rect.remove());
    cleanups.forEach((fn) => fn());
  };
}

export function getNumberedAxisChartEvents(
  points: TrendPoint[] | undefined,
  categories: string[],
  granularity: SalesGranularity,
  timeDuration: string | undefined,
  periodDays: number | undefined,
  handlers: AxisHoverHandlers
) {
  if (!shouldUseNumberedAxisTooltips(granularity, timeDuration, periodDays)) {
    return null;
  }

  let unbind: (() => void) | undefined;

  const sync = (chartContext: { el?: HTMLElement }) => {
    unbind?.();
    handlers.onHide();
    unbind = bindNumberedAxisHoverTooltips(
      chartContext.el,
      points,
      categories,
      granularity,
      timeDuration,
      periodDays,
      handlers
    );
  };

  return {
    mounted: sync,
    updated: sync,
  };
}

export function shouldUseDailyDateAxisLabels(
  granularity: SalesGranularity,
  pointCount: number,
  timeDuration?: string,
  periodDays?: number
): boolean {
  if (granularity !== 'daily' || pointCount <= 0) return false;
  if (shouldUseNumberedDailyAxis(timeDuration, periodDays)) return false;
  return true;
}

export function getDailyAxisLabelFontSize(pointCount: number): string {
  if (pointCount > 90) return '9px';
  if (pointCount > 60) return '10px';
  if (pointCount > 30) return '11px';
  if (pointCount > 14) return '11.5px';
  return '12px';
}

/** Larger axis labels for the View Chart detail modal. */
export function getDetailAxisLabelFontSize(
  pointCount: number,
  granularity: SalesGranularity
): string {
  if (granularity === 'daily') {
    if (pointCount > 60) return '12px';
    if (pointCount > 30) return '13px';
    return '14px';
  }

  if (pointCount > 20) return '12px';
  if (pointCount > 12) return '13px';
  return '14px';
}

function parseLocalDate(dateStr: string): Date | null {
  const isoDate = dateStr.split('T')[0];
  const [year, monthIndex, dayOfMonth] = isoDate.split('-').map(Number);

  if (!year || !monthIndex || !dayOfMonth) {
    return null;
  }

  return new Date(year, monthIndex - 1, dayOfMonth);
}

function formatDayMonth(date: Date, monthStyle: 'long' | 'short' = 'short'): string {
  const month = date.toLocaleString('default', { month: monthStyle });
  return `${date.getDate()} ${month}`;
}

function formatDatePickerDay(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatTimelineSpan(startDateStr: string, endDateStr: string): string {
  const start = parseLocalDate(startDateStr);
  const end = parseLocalDate(endDateStr);

  if (!start || !end) return '';

  const startYear = start.getFullYear();
  const endYear = end.getFullYear();

  const startMonth = start.toLocaleString('default', { month: 'short' });
  const endMonth = end.toLocaleString('default', { month: 'short' });

  const startDayText = `${start.getDate()}${getOrdinalSuffix(start.getDate())} ${startMonth}`;
  const endDayText = `${end.getDate()}${getOrdinalSuffix(end.getDate())} ${endMonth}`;

  if (startYear === endYear) {
    return `${startDayText} to ${endDayText} ${endYear}`;
  }

  return `${startDayText} ${startYear} to ${endDayText} ${endYear}`;
}

/** Overall period shown as a subtitle under the x-axis. */
export function getTimelineSpanTitle(
  points?: TrendPoint[],
  rangeStart?: Date | null,
  rangeEnd?: Date | null
): string {
  if (rangeStart && rangeEnd) {
    const startStr = formatDatePickerDay(rangeStart);
    const endStr = formatDatePickerDay(rangeEnd);

    if (startStr === endStr) {
      return formatChartDailyDate(startStr, { includeYear: true });
    }

    return formatTimelineSpan(startStr, endStr);
  }

  if (!points?.length) return '';

  const firstDate = points[0]?.date;
  const lastDate =
    points[points.length - 1]?.endDate ?? points[points.length - 1]?.date;

  if (!firstDate || !lastDate) return '';

  const firstDay = firstDate.split('T')[0];
  const lastDay = lastDate.split('T')[0];

  if (firstDay === lastDay) {
    return formatChartDailyDate(firstDate, { includeYear: true });
  }

  return formatTimelineSpan(firstDay, lastDay);
}

export function getBucketAxisInterval(pointCount: number): number {
  if (pointCount <= 6) return 1;
  if (pointCount <= 12) return 2;
  if (pointCount <= 20) return 3;
  if (pointCount <= 30) return 4;
  if (pointCount <= 40) return 5;
  return 6;
}

export function shouldShowBucketAxisTick(
  index: number,
  pointCount: number
): boolean {
  if (pointCount <= 0) return false;
  if (index === 0 || index === pointCount - 1) return true;
  return index % getBucketAxisInterval(pointCount) === 0;
}

/** Weekly / monthly charts use real bucket date ranges. Bi-Weekly uses numbers. */
export function shouldUseBucketRangeLabels(
  granularity: SalesGranularity,
  pointCount: number
): boolean {
  return (
    (granularity === 'weekly' || granularity === 'monthly') &&
    pointCount > 0
  );
}

/** @deprecated Use shouldUseBucketRangeLabels */
export function shouldUseWeekRangeLabels(
  _timeDuration: string | undefined,
  granularity: SalesGranularity,
  pointCount = 0
): boolean {
  return shouldUseBucketRangeLabels(granularity, pointCount);
}

export function formatWeekRangeLabel(
  startDateStr: string,
  endDateStr?: string
): string {
  const start = parseLocalDate(startDateStr);
  const end = parseLocalDate(endDateStr || startDateStr);

  if (!start || !end) {
    return startDateStr;
  }

  if (start.getMonth() === end.getMonth()) {
    const month = end.toLocaleString('default', { month: 'short' });
    return `${start.getDate()} to ${end.getDate()} ${month}`;
  }

  return `${formatDayMonth(start)} to ${formatDayMonth(end)}`;
}

export function formatWeekRangeLabelWithYear(
  startDateStr: string,
  endDateStr?: string
): string {
  const start = parseLocalDate(startDateStr);
  const end = parseLocalDate(endDateStr || startDateStr);

  if (!start || !end) {
    return startDateStr;
  }

  const startYear = start.getFullYear();
  const endYear = end.getFullYear();

  if (startYear === endYear) {
    if (start.getMonth() === end.getMonth()) {
      const month = end.toLocaleString('default', { month: 'short' });
      return `${start.getDate()} to ${end.getDate()} ${month} ${endYear}`;
    }
    return `${formatDayMonth(start)} to ${formatDayMonth(end)} ${endYear}`;
  }

  return `${formatDayMonth(start)} ${startYear} to ${formatDayMonth(end)} ${endYear}`;
}

/** X-axis tick labels: dates for short daily ranges, 1..N otherwise. */
export function formatTrendChartLabel(
  dateStr: string,
  granularity: SalesGranularity,
  index: number
): string {
  if (
    granularity === 'weekly' ||
    granularity === 'biweekly' ||
    granularity === 'monthly'
  ) {
    return String(index + 1);
  }

  return dateStr;
}

export function getTrendAxisTitle(
  granularity: SalesGranularity,
  points?: TrendPoint[],
  rangeStart?: Date | null,
  rangeEnd?: Date | null
): string {
  if (rangeStart && rangeEnd) {
    return getTimelineSpanTitle(points, rangeStart, rangeEnd);
  }
  if (!points?.length) return '';
  return getTimelineSpanTitle(points);
}

export function getTrendTooltipPeriodLabel(
  granularity: SalesGranularity,
  index: number,
  category: string,
  dateStr?: string,
  _formatDaily?: (date: string) => string,
  _timeDuration?: string,
  endDateStr?: string,
  _pointCount = 0
): string {
  if (granularity === 'weekly') {
    return dateStr
      ? formatWeekRangeLabelWithYear(dateStr, endDateStr ?? dateStr)
      : `Week ${index + 1}`;
  }
  if (granularity === 'biweekly') {
    return dateStr
      ? formatWeekRangeLabelWithYear(dateStr, endDateStr ?? dateStr)
      : `Bi-Week ${index + 1}`;
  }
  if (granularity === 'monthly') {
    if (dateStr) {
      const date = parseLocalDate(dateStr);
      if (date) {
        const month = date.toLocaleString('default', { month: 'short' });
        return `${month} ${date.getFullYear()}`;
      }
    }
    return `Month ${index + 1}`;
  }
  if (dateStr) {
    const date = parseLocalDate(dateStr);
    if (date) {
      const day = date.getDate();
      const month = date.toLocaleString('default', { month: 'short' });
      return `${day}${getOrdinalSuffix(day)} ${month} ${date.getFullYear()}`;
    }
    return formatChartDailyDate(dateStr);
  }
  return category;
}

export function getNumberedAxisLabelFontSize(pointCount: number): string {
  if (pointCount > 40) return '9px';
  if (pointCount > 20) return '10px';
  if (pointCount > 12) return '11px';
  return '12px';
}

const YEAR_DAYS = 365;

/** Label step for long ranges: 0, 5, 10… or wider for very long custom ranges. */
export function getSparseAxisInterval(pointCount: number): number {
  if (pointCount <= 55) return 5;
  if (pointCount <= 110) return 10;
  if (pointCount <= 165) return 15;
  return 20;
}

/** Sparse ticks (0, 5, 10, 15…) for year+ weekly/monthly views. */
export function shouldUseSparseAxisLabels(
  timeDuration: string | undefined,
  granularity: SalesGranularity,
  pointCount: number,
  periodDays?: number
): boolean {
  const isBucketed =
    granularity === 'weekly' ||
    granularity === 'biweekly' ||
    granularity === 'monthly';
  if (!isBucketed || pointCount <= 6) return false;

  const isLongCustomRange =
    timeDuration === TimePeriod.customRange &&
    periodDays !== undefined &&
    periodDays > YEAR_DAYS;

  if (shouldUseBucketRangeLabels(granularity, pointCount)) {
    return false;
  }

  // Bi-Weekly uses 1..N; sparse only when very crowded
  if (granularity === 'biweekly') {
    return pointCount > 32;
  }

  if (isLongCustomRange) {
    if (granularity === 'weekly') return pointCount > 20;
    if (granularity === 'monthly') return pointCount > 12;
  }

  if (granularity === 'weekly' && pointCount > 52) {
    return true;
  }

  return false;
}

/** Sparse ticks for numbered axes (1, 2, 3… keep first & last). */
export function formatSparseAxisLabel(
  value: string,
  pointCount: number
): string {
  const num = parseInt(value, 10);
  if (Number.isNaN(num)) return '';

  if (num === 1 || num === pointCount) return String(num);

  const interval =
    pointCount <= 40 ? 2 : pointCount <= 60 ? 3 : getSparseAxisInterval(pointCount);
  if (num % interval === 0) return String(num);

  return '';
}

/** @deprecated Use shouldUseSparseAxisLabels */
export function shouldUseSparseYearlyWeekLabels(
  timeDuration: string | undefined,
  granularity: SalesGranularity,
  pointCount: number,
  periodDays?: number
): boolean {
  return shouldUseSparseAxisLabels(
    timeDuration,
    granularity,
    pointCount,
    periodDays
  );
}

/** @deprecated Use formatSparseAxisLabel */
export function formatSparseYearlyWeekLabel(
  value: string,
  pointCount = 52
): string {
  return formatSparseAxisLabel(value, pointCount);
}

export function getTrendChartCategories(
  points: TrendPoint[] | undefined,
  granularity: SalesGranularity,
  _formatDaily: (date: string) => string,
  timeDuration?: string,
  periodDays?: number,
  forceDatedLabels = false
): string[] {
  if (!points?.length) return [];

  return points.map((point, index) => {
    // Detail modal / date-axis views always show real dates
    if (forceDatedLabels) {
      if (granularity === 'daily') {
        return formatChartDailyDate(point.date);
      }
      if (granularity === 'weekly' || granularity === 'biweekly') {
        return formatWeekRangeLabel(point.date, point.endDate ?? point.date);
      }
      if (granularity === 'monthly') {
        const date = parseLocalDate(point.date);
        if (date) {
          return `${date.toLocaleString('default', {
            month: 'short',
          })} ${date.getFullYear()}`;
        }
      }
    }

    if (shouldUseBucketRangeLabels(granularity, points.length)) {
      if (granularity === 'monthly') {
        const date = parseLocalDate(point.date);
        if (date) {
          return `${date.toLocaleString('default', {
            month: 'short',
          })} ${date.getFullYear()}`;
        }
      }
      return formatWeekRangeLabel(point.date, point.endDate ?? point.date);
    }

    if (
      shouldUseNumberedBiWeeklyAxis(granularity) ||
      (granularity === 'daily' &&
        shouldUseNumberedDailyAxis(timeDuration, periodDays))
    ) {
      return String(index + 1);
    }

    if (granularity === 'daily') {
      return formatChartDailyDate(point.date);
    }

    return formatTrendChartLabel(point.date, granularity, index);
  });
}
export function computeOrdersPerPeriod(
  orders: number,
  periodDays: number,
  granularity: SalesGranularity,
  bucketCount?: number
): string | number {
  if (granularity === 'weekly') {
    const weeks = bucketCount ?? Math.max(periodDays / 7, 1);
    return (orders / weeks).toFixed(1);
  }
  if (granularity === 'biweekly') {
    const biWeeks = bucketCount ?? Math.max(periodDays / 14, 1);
    return (orders / biWeeks).toFixed(1);
  }
  if (granularity === 'monthly') {
    const months = bucketCount ?? Math.max(Math.round(periodDays / 30), 1);
    return (orders / months).toFixed(1);
  }
  return parseFloat((orders / Math.max(periodDays, 1)).toFixed(1));
}

export function ordersPerPeriodLabel(granularity: SalesGranularity): string {
  if (granularity === 'weekly') return 'Orders / Week';
  if (granularity === 'biweekly') return 'Orders / Bi-Week';
  if (granularity === 'monthly') return 'Orders / Month';
  return 'Orders / Day';
}
